import React, { useState, useEffect, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons'
import ComponentsUNames from 'constants/componentsUNames'
import LocalStorage from 'utils/myLocalStorage'
import Verdict from '../components/verdict'
import './widgetPPPR.css'
import { Pagination, Table, TableBody, TableHead, TableRow, Toggle } from '@airbus/components-react/components'
import LangContext from 'context/LangContext'
import LoadingContext from 'context/LoadingContext'
import URL_APIS from 'utils/api/urlApis'
import useQuery from 'utils/hooks/useQuery'
import ResponsiveTableCell from 'components/responsiveTableCell'
import config from 'constants/config'
import { useSearchParams } from 'react-router-dom'

const TOLERANCE_ACTIVE = 'Y'
const TOLERANCE_INACTIVE = 'N'
// Separator within the name for each line
const sepNameLines = '__'

const WidgetPPPR = ({ pageUniqueName, selectedAircraft }) => {
  const { t } = useTranslation()

  const { lang } = useContext(LangContext)

  // Page for main aircrafts' widget
  const [searchParams] = useSearchParams()
  const jumpToPage = Number(searchParams.get('page')) || 0

  const { fetchData: fetchPPPR } = useQuery(URL_APIS.URL_FLEETPAN_PPPR_GET)

  const [data, setData] = useState()
  const [isLoadInProgressActive, setIsLoadInProgressActive] = useState(false)
  const [pagination, setPagination] = useState({
    nbItems: 0,
    pageSize: LocalStorage.getOrDefault(
      [
        LocalStorage.KEY_PREFS,
        ComponentsUNames.TABFLOT_STORE_NAME,
        ComponentsUNames.TABFLOT_WDG_PPPR_UNAME + '|' + ComponentsUNames.PROP_PAGE_SIZE,
      ],
      LocalStorage.VALUE_TYPE.INTEGER,
      config.PAGINATION_DEFAULT_PAGESIZE,
    ),
    pageNo: 0,
    nbPages: 0,
    choiceNbRows: [5, 10, 20],
  })

  // Required for showing/hiding children tasks
  const [isLineExpanded, setIsLineExpanded] = useState({})
  // Required for the Tolerance toggle-button
  const [isToleranceActive, setIsToleranceActive] = useState(
    Boolean(
      LocalStorage.getOrDefault(
        [
          LocalStorage.KEY_PREFS,
          ComponentsUNames.TABFLOT_STORE_NAME,
          ComponentsUNames.TABFLOT_WDG_PPPR_UNAME + '|' + ComponentsUNames.PROP_TOLERANCE,
        ],
        LocalStorage.VALUE_TYPE.BOOLEAN,
        false,
      ),
    ),
  )

  /********
   * Synchronization of widget's data to keep track of them
   ********/

  const loadData = async () => {
    setIsLoadInProgressActive(true)
    const response = await fetchPPPR({
      lang,
      avimmatr: selectedAircraft,
      size: pagination.pageSize,
      page: pagination.pageNo,
      tolerance: isToleranceActive ? TOLERANCE_ACTIVE : TOLERANCE_INACTIVE,
    })
    setIsLoadInProgressActive(false)
    const dataFormatted = formatData(response?.data.content)
    setData(dataFormatted)

    if (dataFormatted !== null && dataFormatted !== undefined) {
      let parentLineExpanded = {}
      dataFormatted
        .filter((d) => !d.isChild)
        .forEach((d) => {
          parentLineExpanded[d.id] = false
        })
      setIsLineExpanded(parentLineExpanded)
    }

    setPagination({
      ...pagination,
      nbItems: response?.data.totalElements,
      nbPages: response?.data.totalPages,
      pageNo: response?.data.number,
    })
  }

  /**
   * Flatten dataContent for easier show-up
   * Parent nodes have an ID, whereas children items hold a parentID
   * @param {*} dataContent
   * @returns
   */
  const formatData = (dataContent) => {
    let allDataFormatted = []
    if (dataContent !== undefined && dataContent !== null) {
      dataContent.forEach((d, idParent) => {
        if (d.listTask === undefined) {
          // No counter (no children tasks)
          let dataFormatted = {}
          // Extra properties
          dataFormatted['id'] = idParent
          dataFormatted['isChild'] = false
          dataFormatted['hasChildren'] = false
          dataFormatted['verdict'] = d.verdict
          dataFormatted['remainingPotential'] = d.remainingPotential
          dataFormatted['followUpType'] = d.followUpType

          allDataFormatted.push(dataFormatted)
        } else {
          for (let i = 0; i < d.listTask.length; i++) {
            let dataFormatted = {}
            // Extra properties for showing/hiding children tasks
            if (i === 0) {
              // Parent task
              dataFormatted['id'] = idParent
              dataFormatted['isChild'] = false
              // Display of expand/collapse twisty only if item has got children items to show
              dataFormatted['hasChildren'] = d.listTask.length > 1
            } else {
              // Child task
              dataFormatted['idParent'] = idParent
              dataFormatted['isChild'] = true
            }

            // Main record data
            const task = d.listTask[i]
            dataFormatted['verdict'] = d.verdict
            dataFormatted['remainingPotential'] = d.remainingPotential
            dataFormatted['followUpType'] = d.followUpType
            // Task data
            dataFormatted['taskType'] = task.taskType
            dataFormatted['reference'] = task.reference
            dataFormatted['pn'] = task.pn
            dataFormatted['sn'] = task.sn

            allDataFormatted.push(dataFormatted)
          }
        }
      })
    }

    return allDataFormatted
  }

  const handleToggleShowHide = (e, i) => {
    e.preventDefault()
    setIsLineExpanded({ ...isLineExpanded, [data[i].id]: !isLineExpanded[data[i].id] })
  }

  const handleToggleTolerance = () => {
    setIsToleranceActive(!isToleranceActive)
    // Store as user's preference
    const propToUpdate = [
      LocalStorage.KEY_PREFS,
      pageUniqueName,
      ComponentsUNames.TABFLOT_WDG_PPPR_UNAME + '|' + ComponentsUNames.PROP_TOLERANCE,
    ]
    LocalStorage.put(propToUpdate, !isToleranceActive)
  }

  useEffect(() => {
    if (jumpToPage) {
      setData([])
    } else if (selectedAircraft !== undefined) {
      setIsLoadInProgressActive(true)
      loadData()
    }
  }, [isToleranceActive, selectedAircraft, lang, pagination.pageSize, pagination.pageNo, jumpToPage])

  return (
    <div className='WidgetPPPR'>
      {data && JSON.stringify(data) !== '{}' && JSON.stringify(data) !== '[]' ? (
        <div>
          <div className='tolerance'>
            <div className='tolerance_label'>{t('9500157')}</div>
            <div className='tolerance_icon'>
              <Toggle checked={isToleranceActive} onChange={handleToggleTolerance} />
            </div>
          </div>

          <Table size='small'>
            <colgroup>
              <col style={{ width: '2%' }} />
              <col style={{ width: '3%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '7%' }} />
            </colgroup>
            <TableHead>
              <TableRow>
                <ResponsiveTableCell head></ResponsiveTableCell>
                <ResponsiveTableCell head className='expandCollapse'></ResponsiveTableCell>
                <ResponsiveTableCell head align='right'>
                  {t('9500045')}
                </ResponsiveTableCell>
                <ResponsiveTableCell head>{t('9500044')}</ResponsiveTableCell>
                <ResponsiveTableCell head>{t('9500046')}</ResponsiveTableCell>
                <ResponsiveTableCell head>{t('9500042')}</ResponsiveTableCell>
                <ResponsiveTableCell head>{t('9500047')}</ResponsiveTableCell>
                <ResponsiveTableCell head>{t('9500048')}</ResponsiveTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data ? (
                data.map(
                  (d, i) =>
                    (!d?.isChild || (d?.idParent !== undefined && isLineExpanded[d?.idParent] === true)) && (
                      <TableRow
                        id={'pppr_line_' + i}
                        key={'pppr_line_' + i}
                        name={ComponentsUNames.TABFLOT_WDG_PPPR_UNAME + '_line_pppr' + sepNameLines + i}
                        style={{
                          cursor: 'pointer',
                        }}
                      >
                        <ResponsiveTableCell align='center'>
                          <Verdict value={d.hasOwnProperty('verdict') ? d.verdict : ''} />
                        </ResponsiveTableCell>
                        <ResponsiveTableCell align='center' onClick={(e) => handleToggleShowHide(e, i)}>
                          {!d.isChild && d.hasChildren && (
                            <div>
                              {isLineExpanded[d.id] ? (
                                <FontAwesomeIcon icon={faChevronUp} />
                              ) : (
                                <FontAwesomeIcon icon={faChevronDown} />
                              )}
                            </div>
                          )}
                        </ResponsiveTableCell>
                        <ResponsiveTableCell
                          data-negative={d.remainingPotential ? d.remainingPotential < 0 : false}
                          align='right'
                        >
                          {d.hasOwnProperty('remainingPotential') ? d.remainingPotential : '-'}
                        </ResponsiveTableCell>
                        <ResponsiveTableCell>
                          {d.hasOwnProperty('followUpType') ? d.followUpType : '-'}
                        </ResponsiveTableCell>
                        <ResponsiveTableCell>{d.hasOwnProperty('taskType') ? d.taskType : '-'}</ResponsiveTableCell>
                        <ResponsiveTableCell>{d.hasOwnProperty('reference') ? d.reference : '-'}</ResponsiveTableCell>
                        <ResponsiveTableCell>{d.hasOwnProperty('pn') ? d.pn : '-'}</ResponsiveTableCell>
                        <ResponsiveTableCell>{d.hasOwnProperty('sn') ? d.sn : '-'}</ResponsiveTableCell>
                      </TableRow>
                    ),
                )
              ) : (
                <TableRow />
              )}
            </TableBody>

            <TableRow>
              <ResponsiveTableCell colSpan='8'>
                <Pagination
                  count={pagination.nbItems}
                  page={pagination.pageNo + 1}
                  onPageChange={(e, newPage) => setPagination({ ...pagination, pageNo: newPage - 1 })}
                  rowsPerPage={pagination.pageSize}
                  onRowsPerPageChange={(e, newRowPerPage) => {
                    LocalStorage.put(
                      [LocalStorage.KEY_PREFS, pageUniqueName, ComponentsUNames.TABFLOT_WDG_PPPR_UNAME + '|PAGESIZE'],
                      newRowPerPage,
                    )
                    setPagination({ ...pagination, pageSize: parseInt(newRowPerPage), pageNo: 0 })
                  }}
                  rowsPerPageOptions={pagination.choiceNbRows}
                  showRowsPerPage
                  rowsPerPageLabel={t('entries_per_page')}
                />
              </ResponsiveTableCell>
            </TableRow>
          </Table>

          <div></div>
        </div>
      ) : (
        <div style={{ paddingLeft: '1em' }}>{isLoadInProgressActive ? t('9500166') : t('9500055')}</div>
      )}
    </div>
  )
}

export default WidgetPPPR
